/**
 * Canonical brand identity facts — the single source of truth for the
 * structured-data schemas (lib/seo.ts) and the public /api/brand endpoint.
 *
 * Contact details and social handles already live in lib/config.ts; this
 * module references those rather than copying them, so there is exactly one
 * place to edit each fact. Keep this PII-free and secret-free — everything
 * here is published verbatim at /api/brand.
 */

import { config } from "@/lib/config";

/** Days of the week in OpeningHoursSpecification order. `as const` keeps the
 *  literal day names so schema-dts accepts them as `DayOfWeek` in lib/seo.ts. */
const ALL_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

/** A geographic pin. Only ever published PAIRED with the postcode it belongs to. */
export interface BusinessGeo {
  latitude: number;
  longitude: number;
}

/** Shape of the owner-set studio address (see STUDIO_ADDRESS). */
export interface StudioAddress {
  /**
   * The studio's own postcode — a full postcode ("LS11 5AA") or, if the owner
   * prefers not to publish a precise one, the district ("LS11").
   */
  postalCode?: string;
  /**
   * Coordinates of `postalCode`. Leave unset if unknown: `lib/seo.ts` then omits
   * `geo` from the schema entirely rather than publishing a pin that contradicts
   * the postcode. An absent property is honest; a contradictory one is not.
   */
  geo?: BusinessGeo;
  /**
   * Street line. Deliberately unset — publishing the street is the owner's
   * decision, and matching the Google Business Profile street EXACTLY is the
   * only version worth publishing (a near-match is worse than none for NAP).
   */
  streetAddress?: string;
}

/**
 * ───────────────────────────────────────────────────────────────────────────
 * ⚠️  OWNER ACTION REQUIRED — the studio's own postcode.
 *
 * The business is a COMMERCIAL STUDIO in Leeds (LS11 / Holbeck,
 * owner-confirmed 2026-08-02) and is already published on the Google Business
 * Profile. Set `postalCode` below to the studio's real postcode and, if you
 * have them, its coordinates.
 *
 * WHAT CHANGES WHEN YOU SET IT:
 *   • `postalCode` only          → the Florist/LocalBusiness JSON-LD publishes
 *                                  that postcode and publishes NO `geo` at all
 *                                  (an unpaired coordinate would contradict it).
 *   • `postalCode` + `geo`       → both are published, from this one source.
 *   • `streetAddress` too        → a `streetAddress` is added to the schema.
 *   • left empty (today)         → the pre-2026-08-02 rendered output is kept
 *                                  verbatim: postcode "LS1" + the Leeds
 *                                  city-centre service-area pin. Nothing
 *                                  regresses, but the NAP still does not match
 *                                  the Google Business Profile.
 *
 * A PLACEHOLDER HERE WOULD BE WORSE THAN THE BUG IT REPLACES — never guess a
 * postcode, a street or a coordinate. Publishing an address the business does
 * not occupy is a harder local-ranking problem than publishing a vague one.
 *
 * TWO EXISTING SPECS PIN THE FALLBACK PIN and must be updated in the SAME change
 * that fills this in (they assert the old city-centre coordinate, so they are the
 * "did you mean to move the business?" tripwire, not a reason to leave it empty):
 *   • tests/unit/lib/seo-entity-graph.spec.ts — asserts geo ≈ 53.8, -1.55;
 *   • tests/unit/app/zh-landing-schema.spec.tsx (ZHENTITY-1) — asserts the /zh
 *     Florist node HAS a `geo`, which no longer holds if a postcode is set with
 *     no coordinate beside it.
 * Coordinates for a UK postcode are available from postcodes.io, which is already
 * integrated for delivery pricing — look one up ONCE and paste it here. Do NOT
 * add a network call to a page render for this; a static constant is correct.
 * ───────────────────────────────────────────────────────────────────────────
 */
export const STUDIO_ADDRESS: StudioAddress = {
  // postalCode: "LS11 ...",                          // ← OWNER: set this
  // geo: { latitude: 53.____, longitude: -1.____ },  // ← optional, must match ↑
  // streetAddress: "...",                            // ← optional, GBP-exact only
};

/**
 * Pre-studio fallback: the Leeds city-centre service-area pin the site rendered
 * up to 2026-08-02. Used ONLY while `STUDIO_ADDRESS.postalCode` is unset, so
 * filling that in is a pure improvement and leaving it empty is a no-op.
 *
 * The district + the city-centre coordinate are consistent with each other
 * (LS1 *is* the city centre), which is why this pair is safe to keep publishing.
 */
const SERVICE_AREA_FALLBACK: { postalCode: string; geo: BusinessGeo } = {
  postalCode: "LS1",
  geo: { latitude: 53.8008, longitude: -1.5491 },
};

/**
 * Resolve the canonical business address's variable parts from the single
 * `STUDIO_ADDRESS` constant. Postcode and geo are resolved TOGETHER so they
 * cannot be sourced from different places — that pairing is the whole point.
 */
function resolveBusinessLocation(): {
  postalCode: string;
  geo: BusinessGeo | undefined;
  streetAddress: string | undefined;
  isStudioAddress: boolean;
} {
  const studioPostcode = STUDIO_ADDRESS.postalCode?.trim();
  if (studioPostcode) {
    return {
      postalCode: studioPostcode,
      // May be undefined — deliberately. Consumers omit `geo` rather than
      // falling back to the city-centre pin, which would contradict this
      // postcode. Pair, never mix.
      geo: STUDIO_ADDRESS.geo,
      streetAddress: STUDIO_ADDRESS.streetAddress?.trim() || undefined,
      isStudioAddress: true,
    };
  }
  return {
    postalCode: SERVICE_AREA_FALLBACK.postalCode,
    geo: SERVICE_AREA_FALLBACK.geo,
    streetAddress: undefined,
    isStudioAddress: false,
  };
}

const businessLocation = resolveBusinessLocation();

export const brand = {
  name: "The Floral Muse",
  /**
   * Registered company name. Consumed by the Organization JSON-LD
   * (`legalName`, lib/seo.ts), the public /api/brand payload, and — once the
   * three fields below are filled — the footer's trading-disclosure line.
   */
  legalName: "Floral Muse by Joyce Ltd",
  /** Named on /about ("From the founder") and in the Organization schema. */
  founder: "Joyce",

  // ───────────────────────────────────────────────────────────────────────────
  // ⚠️  OWNER ACTION REQUIRED — company-identity disclosure.
  //
  // If `legalName` above is an INCORPORATED company, UK law requires the
  // registered name, registered number, place of registration and registered
  // office address to appear on the website:
  //   • Companies Act 2006 s.82 + The Company, Limited Liability Partnership
  //     and Business (Names and Trading Disclosures) Regulations 2015, and
  //   • The Electronic Commerce (EC Directive) Regulations 2002, reg 6.
  //
  // STATUS (owner-confirmed 2026-07-25): name, number and place of registration
  // are set. `registeredOffice` is deliberately left EMPTY at the owner's
  // instruction — the registered office is a residential address and they did
  // not want it indexed in a site footer.
  //
  // ⚠️  KNOWN PARTIAL COMPLIANCE: s.82 wants the registered office too, so the
  // disclosure below is incomplete as it stands. The fix is NOT to publish a home
  // address — it is to change the registered office at Companies House to a
  // non-residential one (an accountant's address, or a registered-office service,
  // typically £30-60/yr; the filing itself is free). Once that is done, set
  // `registeredOffice` here and the footer completes itself with no further code
  // change. Note the number and the current registered office are ALREADY public
  // on the Companies House register — withholding them here does not make them
  // private, it only leaves the site's own disclosure short.
  //
  // The footer renders whatever is set and omits what isn't, so nothing here can
  // produce an empty or half-built line (guarded by LEGAL-4/4b/5 in
  // tests/unit/components/Footer.spec.tsx).
  // ───────────────────────────────────────────────────────────────────────────
  /**
   * Registered company number (Companies House). Public information — the same
   * value anyone can retrieve by searching the register.
   */
  companyNumber: "16753952" as string | undefined,
  /**
   * Place of registration. Determined from the number's format: 8 digits with no
   * letter prefix is England and Wales (Scotland uses an `SC` prefix, Northern
   * Ireland `NI`).
   */
  placeOfRegistration: "England and Wales" as string | undefined,
  /**
   * Registered office address (single line). INTENTIONALLY UNSET — see the
   * KNOWN PARTIAL COMPLIANCE note above. Set this only once the registered
   * office on the Companies House register is a non-residential address.
   */
  registeredOffice: undefined as string | undefined,

  taglines: {
    /** Locator + USP micro-tagline (Branding BN1). Org `slogan`. */
    slogan: "Bespoke Leeds florist — crochet & preserved flowers, UK-wide",
    /** One-line business description (Org/WebSite `description`). */
    short:
      "Bespoke florist in Leeds — fresh flower bouquets, preserved flowers, and hand-crocheted florals.",
    /** Long-form, keyword-rich line used to pad thin meta descriptions. */
    long: "Handcrafted in Leeds by The Floral Muse — fresh bouquets, preserved flowers & crochet florals, delivered locally or shipped UK-wide.",
  },

  // ───────────────────────────────────────────────────────────────────────────
  // CANONICAL BUSINESS ADDRESS — the ONE source for the Florist/LocalBusiness
  // `address` AND `geo` (lib/seo.ts) and for the /api/brand payload.
  //
  // This comment used to describe an online-only service-area business with no
  // street. That was OUT OF DATE: the business is a real commercial studio in
  // Leeds (owner-confirmed 2026-08-02), already published on the Google Business
  // Profile. Its postcode/geo come from `STUDIO_ADDRESS` above — fill that in,
  // not this object.
  //
  // ⚠ COLLECTION POINTS ARE NOT THE BUSINESS ADDRESS, and must never feed this.
  // The walk-up collection point (about a minute's walk from the studio, on the
  // other side of the LS1/LS11 boundary) and the by-car point are OPERATIONAL
  // facts. They live in admin Settings → Delivery and reach customers through
  // `resolveCollectionPoints` on /delivery-information, /faq and checkout — not
  // through schema.org `address`.
  //
  // THE DEFECT THIS REPLACES (fixed 2026-08-02): `app/(main)/page.tsx` built
  // `address.postalCode` from `resolveCollectionPoints(settings.collection)[0]`,
  // so the Florist node published a collection point as the business address
  // (rendering "LS1 4EY"), while `geo` stayed hardcoded to a generic Leeds
  // city-centre coordinate that never moved with it. One node therefore asserted
  // a specific postcode and an unrelated pin, and neither matched the studio
  // address on the Google Business Profile. Inconsistent NAP across sources is a
  // direct local-ranking negative.
  // ───────────────────────────────────────────────────────────────────────────
  location: {
    locality: "Leeds",
    region: "West Yorkshire",
    /**
     * The business postcode: the studio's once `STUDIO_ADDRESS.postalCode` is
     * set, otherwise the "LS1" service-area district rendered before 2026-08-02.
     */
    postalCode: businessLocation.postalCode,
    country: "GB",
    /**
     * Coordinate PAIRED with `postalCode` — `undefined` means "publish no geo".
     * Consumers MUST treat it that way rather than substituting a pin of their
     * own; an absent property is honest, a contradictory one is not.
     */
    geo: businessLocation.geo,
    /**
     * Street line — `undefined` unless the owner sets
     * `STUDIO_ADDRESS.streetAddress`. Nothing invents one.
     */
    streetAddress: businessLocation.streetAddress,
    /**
     * `true` once the real studio postcode is configured. Lets a consumer tell
     * "precise business address" from "service-area district fallback" without
     * pattern-matching the postcode string.
     */
    isStudioAddress: businessLocation.isStudioAddress,
  },

  /** Plain-string service area (the schemas wrap these as place entities). */
  areaServed: ["Leeds", "West Yorkshire", "United Kingdom"],

  /**
   * schema.org `priceRange` band.
   *
   * MUST be denominated in the currency the business actually trades in. This
   * read `"$$"` until 2026-08-01, which on a GBP-only shop (`currenciesAccepted:
   * "GBP"`, every price rendered by `formatPrice` as £) advertised a dollar
   * price band — a currency contradiction inside our own LocalBusiness node.
   *
   * Single source of truth: consumed by the Florist JSON-LD (`lib/seo.ts`) and
   * the public /api/brand payload, so the fix propagates to both.
   */
  priceRange: "££",

  /** Content languages the site publishes in. */
  languages: ["en-GB", "zh-CN"],

  /**
   * Enquiry availability. The studio is a workroom, not a walk-in shopfront,
   * so these are the hours we answer enquiries and hand over collections.
   * `days` + `opens`/`closes` feed OpeningHoursSpecification; `display` is the
   * human-readable form shown in copy (e.g. the FAQ).
   */
  hours: {
    days: ALL_DAYS,
    opens: "09:00",
    closes: "17:00",
    display: "Mon–Sun, 9am–5pm",
  },
};

/**
 * The exact JSON shape served at /api/brand. Defined ONCE here so the route
 * and any internal consumer share a single contract. Composed from `brand`
 * (identity facts) + `config` (contact details / handles / links) — there is
 * no third copy of any value. Bump `version` on a breaking shape change.
 */
export interface BrandPayload {
  version: number;
  name: string;
  legalName: string;
  founder: string;
  url: string;
  description: string;
  taglines: { slogan: string; short: string; long: string };
  contact: { email: string; phone: string; phoneDisplay: string; whatsapp: string };
  handles: {
    instagram: string;
    facebook: string;
    tiktok: string;
    pinterest: string;
    xiaohongshu: string;
    wechatId: string;
  };
  hours: { days: string[]; opens: string; closes: string; display: string };
  location: { locality: string; region: string; postalCode: string; country: string };
  areaServed: string[];
  languages: string[];
  priceRange: string;
  links: { googleBusiness: string; googleReview: string; trustpilot: string; whatsappCatalog: string };
}

export function getBrandPayload(): BrandPayload {
  return {
    version: 1,
    name: brand.name,
    legalName: brand.legalName,
    founder: brand.founder,
    url: config.siteUrl,
    description: brand.taglines.short,
    taglines: {
      slogan: brand.taglines.slogan,
      short: brand.taglines.short,
      long: brand.taglines.long,
    },
    contact: {
      email: config.contactEmail,
      phone: config.phone, // E.164
      phoneDisplay: config.phoneDisplay, // UK national format
      whatsapp: config.whatsapp,
    },
    handles: {
      instagram: config.instagram,
      facebook: config.facebook,
      tiktok: config.tiktok,
      pinterest: config.pinterest,
      xiaohongshu: config.xiaohongshu,
      // WeChat has no public profile URL — share the ID for "add by ID".
      wechatId: config.wechatId,
    },
    // Fresh arrays so a consumer can't mutate the shared `brand` constants.
    hours: {
      days: [...brand.hours.days],
      opens: brand.hours.opens, // 24h "HH:MM"
      closes: brand.hours.closes,
      display: brand.hours.display,
    },
    location: {
      locality: brand.location.locality,
      region: brand.location.region,
      postalCode: brand.location.postalCode,
      country: brand.location.country,
    },
    areaServed: [...brand.areaServed],
    languages: [...brand.languages],
    priceRange: brand.priceRange,
    links: {
      googleBusiness: config.googleBusinessUrl,
      googleReview: config.googleReviewUrl,
      trustpilot: config.trustpilotUrl,
      whatsappCatalog: config.whatsappCatalog,
    },
  };
}
